import { useState, useEffect } from 'react'
import api from '../repositories/api'
import { toDateKey } from './accuracy'

const INPUT_PAGE_SIZE = 35

/**
 * Custom hook that fetches inputs (ERP sales data) paginated
 * until all the dates present in `days` are covered.
 *
 * @param {string} storeId
 * @param {string|null} token
 * @param {Array} days - array of day objects, each with a `date` field
 * @returns {{ inputsByDate: Object, loading: boolean, error: boolean }}
 *   inputsByDate is keyed by 'YYYY-MM-DD' -> IInput
 */
export default function useInputsForDays(storeId, token, days) {
  const [inputsByDate, setInputsByDate] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!storeId || !token || !days || days.length === 0) {
      setInputsByDate({})
      setError(false)
      return
    }

    const targetDates = new Set(days.map(d => toDateKey(d.date)))

    let cancelled = false
    setLoading(true)
    setError(false)

    async function fetchAllPages() {
      const result = {}
      let page = 1
      let hasMore = true
      let failed = false

      while (hasMore && !cancelled) {
        try {
          const response = await api.axios.get(`/input/store/${storeId}`, {
            params: { sort: '-date', limit: INPUT_PAGE_SIZE, page },
            headers: { Authorization: `Bearer ${token}` }
          })

          const { items, metadata } = response.data

          items.forEach(input => {
            const dateKey = toDateKey(input.date)
            if (!result[dateKey]) {
              result[dateKey] = input
            }
          })

          const allCovered = [...targetDates].every(d => result[d])
          if (allCovered || page >= metadata.pagination.pageCount) {
            hasMore = false
          } else {
            page++
          }
        } catch (e) {
          failed = true
          hasMore = false
        }
      }

      if (!cancelled) {
        setInputsByDate(result)
        setError(failed)
        setLoading(false)
      }
    }

    fetchAllPages()

    return () => {
      cancelled = true
    }
  }, [storeId, token, days])

  return { inputsByDate, loading, error }
}
